import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { ManageConnectionService } from '../../../manage-connection-group/services/manage-connection.service';
import { AuthService } from '../../../auth/services/auth.service';
import { takeUntil } from 'rxjs';
import {
  CdfiProfile,
  HistoryChange,
  ProfileHistoryItem,
} from '../../models/profile.model';
import { ProfileService } from '../../services/profile.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-cdfi-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ClickOutsideDirective, PopupComponent],
  templateUrl: './cdfi-profile.component.html',
  styleUrl: './cdfi-profile.component.scss',
})
export class CdfiProfileComponent
  extends MenuComponentBase
  implements OnInit, AfterViewChecked
{
  imageService = inject(ImageService);
  profileService = inject(ProfileService);
  manageConnectionService = inject(ManageConnectionService);
  authService = inject(AuthService);
  cdr = inject(ChangeDetectorRef);
  commonService = inject(CommonService);

  @ViewChild('descriptionText') descriptionTextElement!: ElementRef;

  cdfiProfile: CdfiProfile | null = null;
  connectionLogoUrl: string | null = null;
  cdfiProfileLoading = false;
  cdfiProfileSaving = false;
  cdfiProfileLoaded = false;
  cdfiProfileError = '';
  cdfiForm: CdfiProfile = {
    name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
  };
  selectedLogoPreview: string | null = null;
  selectedLogoFile: File | null = null;
  originalCdfiForm: CdfiProfile = { ...this.cdfiForm };
  isCdfiLogoDropdownOpen = false;
  showDeleteCdfiLogoPopup = false;
  connectionName: string | undefined = '';
  isDescriptionOverflowing = false;

  isEditMode = false;
  isDescriptionExpanded = false;

  historyItems: ProfileHistoryItem[] = [];
  isHistoryLoading = false;
  isLoadingMore = false;
  currentPage = 1;
  pageSize = 10;
  hasMoreHistory = false;
  isHistoryExpanded = false;
  historyError = '';

  readonly EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  readonly PHONE_REGEX = /^\+?[\d\s-()]{10,}$/;
  readonly WEBSITE_REGEX =
    /^(https?:\/\/)?([\da-z.-]+)\.([\da-z.]{2,6})([/\w .-]*)*\/?$/i;

  override ngOnInit(): void {
    super.ngOnInit();
    this.commonService.connectionName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        if (name) {
          this.cdfiForm.name = name;
          this.connectionName = name;
        }
      });
    this.imageService.connectionImageUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.connectionLogoUrl = url;
      });
    this.callCdfiProfileApi();
  }

  ngAfterViewChecked(): void {
    this.checkDescriptionOverflow();
  }

  private checkDescriptionOverflow(): void {
    if (this.descriptionTextElement && !this.isDescriptionExpanded) {
      const element = this.descriptionTextElement.nativeElement;
      const overflowing = element.scrollHeight > element.clientHeight;
      if (this.isDescriptionOverflowing !== overflowing) {
        this.isDescriptionOverflowing = overflowing;
        this.cdr.detectChanges();
      }
    }
  }

  get isCdfiFormValid(): boolean {
    const isNameValid = this.cdfiForm.name.trim().length > 0;
    const isEmailValid =
      !this.cdfiForm.email || this.EMAIL_REGEX.test(this.cdfiForm.email);
    const isPhoneValid =
      !this.cdfiForm.phone || this.PHONE_REGEX.test(this.cdfiForm.phone);
    const isWebsiteValid =
      !this.cdfiForm.website || this.WEBSITE_REGEX.test(this.cdfiForm.website);
    const isDescriptionValid =
      !this.cdfiForm.description || this.cdfiForm.description.trim().length > 0;

    return (
      isNameValid &&
      isEmailValid &&
      isPhoneValid &&
      isWebsiteValid &&
      isDescriptionValid
    );
  }

  private callCdfiProfileApi(): void {
    this.cdfiProfileLoading = true;
    this.cdfiProfileError = '';

    this.profileService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          this.cdfiProfile = profile;
          this.patchCdfiForm(profile);
          this.connectionName = profile.name;
          this.commonService.updateConnectionName(profile.name);
          this.cdfiProfileLoading = false;
          this.cdfiProfileLoaded = true;
          this.loadHistory(this.currentPage);
        },
        error: (error) => {
          this.cdfiProfile = null;
          this.cdfiProfileLoading = false;
          this.cdfiProfileLoaded = true;
          this.cdfiProfileError =
            error?.error?.detail || 'Unable to load your Organization profile.';
          this.showToastMessage(this.cdfiProfileError, 'error');
        },
      });
  }

  saveCdfiProfile(): void {
    if (!this.cdfiProfile?.id || !this.cdfiProfile || this.cdfiProfileSaving) {
      return;
    }

    const payload = {
      name: this.cdfiForm.name.trim(),
      description: this.normalizeOptionalField(this.cdfiForm.description),
      email: this.normalizeOptionalField(this.cdfiForm.email),
      phone: this.normalizeOptionalField(this.cdfiForm.phone),
      website: this.normalizeOptionalField(this.cdfiForm.website),
    };

    this.cdfiProfileSaving = true;
    this.cdfiProfileError = '';

    this.profileService
      .updateProfile(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          // Immediately apply the updated data so the UI reflects changes, without waiting for the history API response.
          this.cdfiProfile = profile;
          this.commonService.updateConnectionName(profile.name);
          this.patchCdfiForm(this.cdfiProfile);
          this.cdfiProfileSaving = false;
          this.showToastMessage(
            'Organization profile updated successfully.',
            'success',
          );
          this.isEditMode = false;
          // Reload history silently (no loader) after save
          this.reloadHistorySilently();
        },
        error: (error) => {
          this.cdfiProfileSaving = false;
          this.cdfiProfileError = this.getErrorMessage(error);
          this.showToastMessage(this.cdfiProfileError, 'error');
        },
      });
  }

  resetCdfiProfileForm(): void {
    if (this.cdfiProfile) {
      this.patchCdfiForm(this.cdfiProfile);
    }
    this.isEditMode = false;
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      this.resetCdfiProfileForm();
    }
    this.cdfiProfileError = '';
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
    if (!this.isDescriptionExpanded) {
      setTimeout(() => this.checkDescriptionOverflow(), 0);
    }
  }

  private patchCdfiForm(profile: CdfiProfile): void {
    this.cdfiForm.name = profile.name ?? '';
    this.cdfiForm.description = profile.description ?? '';
    this.cdfiForm.email = profile.email ?? '';
    this.cdfiForm.phone = profile.phone ?? '';
    this.cdfiForm.website = profile.website ?? '';
    this.originalCdfiForm = JSON.parse(JSON.stringify(this.cdfiForm));
  }

  get cdfiLogoSrc(): string | null {
    if (this.selectedLogoPreview) {
      return this.selectedLogoPreview;
    }
    if (this.connectionLogoUrl) {
      return this.connectionLogoUrl;
    }
    return null;
  }

  onCdfiLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      input.value = '';
      return;
    }

    const file = input.files[0];
    const validExtensions = ['jpg', 'jpeg', 'png', 'svg'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileSizeMB = file.size / (1024 * 1024);

    if (!fileExtension || !validExtensions.includes(fileExtension)) {
      this.showToastMessage(
        'Invalid file type. Please select a JPG, JPEG, PNG, or SVG image.',
        'error',
      );
      input.value = '';
      return;
    }

    if (fileSizeMB > 10) {
      this.showToastMessage('Logo file size exceeds 10MB limit.', 'error');
      input.value = '';
      return;
    }

    this.selectedLogoPreview = URL.createObjectURL(file);

    this.imageService
      .uploadConnectionImage(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeCdfiLogoDropdown();
          this.selectedLogoPreview = null;
          this.showToastMessage('Logo uploaded successfully', 'success');
          input.value = '';
        },
        error: (err) => {
          this.showToastMessage(
            err.error?.detail || 'Failed to upload logo',
            'error',
          );
          input.value = '';
        },
      });
  }

  onCdfiLogoIconClick(logoInput: HTMLInputElement) {
    // If no CDFI logo exists, directly open file picker
    if (!this.cdfiLogoSrc) {
      logoInput.click();
    } else {
      // If logo exists, show dropdown menu
      this.toggleCdfiLogoDropdown();
    }
  }

  toggleCdfiLogoDropdown() {
    this.isCdfiLogoDropdownOpen = !this.isCdfiLogoDropdownOpen;
  }

  closeCdfiLogoDropdown() {
    this.isCdfiLogoDropdownOpen = false;
  }

  deleteCdfiLogo() {
    this.showDeleteCdfiLogoPopup = true;
    this.closeCdfiLogoDropdown();
  }

  onConfirmDeleteCdfiLogo() {
    this.imageService.deleteConnectionImage();
    this.connectionLogoUrl = null;
    this.selectedLogoPreview = null;
    this.showDeleteCdfiLogoPopup = false;
  }

  onCancelDeleteCdfiLogo() {
    this.showDeleteCdfiLogoPopup = false;
  }

  private normalizeOptionalField(
    value: string | null | undefined,
  ): string | null {
    if (!value) return null;
    const trimmedValue = value.trim();
    return trimmedValue === '' ? null : trimmedValue;
  }

  showToastMessage(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message: message,
    });
  }

  get isCdfiFormChanged(): boolean {
    const trim = (v: string | null | undefined) => (v ?? '').trim();
    return (
      trim(this.cdfiForm.name) !== trim(this.originalCdfiForm.name) ||
      trim(this.cdfiForm.description) !==
        trim(this.originalCdfiForm.description) ||
      trim(this.cdfiForm.email) !== trim(this.originalCdfiForm.email) ||
      trim(this.cdfiForm.phone) !== trim(this.originalCdfiForm.phone) ||
      trim(this.cdfiForm.website) !== trim(this.originalCdfiForm.website)
    );
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error?.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (error.error.detail) {
        return error.error.detail;
      }
      // Handle field-specific validation errors from backend
      if (typeof error.error === 'object') {
        const firstKey = Object.keys(error.error)[0];
        const firstError = error.error[firstKey];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return `${firstKey}: ${firstError[0]}`;
        }
        if (typeof firstError === 'string') {
          return `${firstKey}: ${firstError}`;
        }
      }
    }
    return 'Unable to update your Organization profile.';
  }

  loadHistory(currentPage: number): void {
    if (currentPage === 1) {
      this.historyItems = [];
    }
    this.historyError = '';
    this.isHistoryLoading = true;
    this.profileService
      .getHistory(currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const newItems = response.results;
          this.historyItems = [...this.historyItems, ...newItems];
          this.processHistoryItems();
          this.hasMoreHistory = !!response.next;
          this.isHistoryLoading = false;
        },
        error: (error) => {
          this.isHistoryLoading = false;
          this.historyError = this.getErrorMessage(error);
        },
      });
  }

  /** Reload page 1 of history without showing the skeleton loader. */
  private reloadHistorySilently(): void {
    this.currentPage = 1;
    this.profileService
      .getHistory(1, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.historyItems = response.results;
          this.processHistoryItems();
          this.hasMoreHistory = !!response.next;
        },
        error: () => {
          // silently ignore – existing history items remain
        },
      });
  }

  private processHistoryItems(): void {
    // For each item, identify what changed relative to the previous (older) version
    for (let i = 0; i < this.historyItems.length; i++) {
      const current = this.historyItems[i];
      const previous = this.historyItems[i + 1];

      current.changes = this.getChangedFields(current, previous);

      if (current.changes?.length) {
        current.fieldListSummary = current.changes
          .map((c) => c.label.toLowerCase())
          .join(', ');
        current.summary = `${current.changed_by_name} updated ${current.fieldListSummary}`;
      } else {
        current.summary = `${current.changed_by_name} viewed or saved without changes`;
      }
    }
  }

  getChangedFields(
    item: ProfileHistoryItem,
    prevItem: ProfileHistoryItem | undefined,
  ): HistoryChange[] {
    const fields = [
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'website', label: 'Website' },
    ] as const;

    const changes: HistoryChange[] = [];
    for (const field of fields) {
      const newValue =
        item.changed_data[field.key as keyof typeof item.changed_data];
      const oldValue = prevItem
        ? prevItem.changed_data[field.key as keyof typeof prevItem.changed_data]
        : null;

      // If there's no previous item, we only show fields that have a value
      if (!prevItem) {
        if (newValue) {
          changes.push({
            label: field.label,
            oldValue: null,
            newValue: (newValue as string) || 'None',
          });
        }
        continue;
      }

      if (newValue !== oldValue) {
        changes.push({
          label: field.label,
          oldValue: (oldValue as string) || 'None',
          newValue: (newValue as string) || 'None',
        });
      }
    }
    return changes;
  }

  loadMoreHistory(): void {
    if (this.isLoadingMore || !this.hasMoreHistory) {
      return;
    }
    this.currentPage++;
    this.isLoadingMore = true;
    this.profileService
      .getHistory(this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.historyItems = [...this.historyItems, ...response.results];
          this.processHistoryItems();
          this.hasMoreHistory = !!response.next;
          this.isLoadingMore = false;
        },
        error: () => {
          this.currentPage--;
          this.isLoadingMore = false;
        },
      });
  }

  toggleHistory(): void {
    this.isHistoryExpanded = !this.isHistoryExpanded;
  }

  getWebsiteUrl(url: string | null | undefined): string {
    if (!url) return '';
    const trimmedUrl = url.trim();
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }
    return 'https://' + trimmedUrl;
  }
}
